import React, { useEffect, useState, useRef } from 'react';
import { useFirebase } from './FirebaseProvider';
import { subscribeToAccounts, subscribeToTransactions } from '../services/financeService';
import { Account, Transaction } from '../types';
import { getInstitutionById } from '../constants/institutions';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowRightLeft, 
  Plus, 
  Wallet, 
  Banknote, 
  CreditCard, 
  MoreHorizontal,
  LogOut,
  Link as LinkIcon,
  RefreshCw,
  History,
  Archive,
  AlertTriangle,
  Edit2,
  Terminal,
  CalendarDays,
  BookOpen,
  Briefcase,
  Settings,
  ArrowLeft,
  Filter,
  Eye,
  EyeOff,
  Calculator
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { safeFormat } from '../utils/dateUtils';
import { TransactionForm } from './TransactionForm';
import { UserManagement } from './UserManagement';
import { AITransactionInput } from './AITransactionInput';
import { AccountAudit } from './AccountAudit';
import { EditAccountModal } from './EditAccountModal';
import { AddAccountModal } from './AddAccountModal';
import { TransactionHistory } from './TransactionHistory';
import { SystemLogsViewer } from './SystemLogsViewer';
import { SystemSettingsPanel } from './SystemSettingsPanel';
import { PlanningDashboard } from './planning/PlanningDashboard';
import { AccountingDashboard } from './accounting/AccountingDashboard';
import { CreditCardReport } from './CreditCardReport';
import { ManagementDashboard } from './management/ManagementDashboard';
import { CashCountTool } from './tools/CashCountTool';
import { AccountIcon } from './AccountIcon';
import { signOut } from '@potencial/supabase';
import { resetPeriod } from '../services/financeService';

export const Dashboard: React.FC = () => {
  const { user, profile, effectiveUserId } = useFirebase();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [showForm, setShowForm] = useState<'INCOME' | 'EXPENSE' | 'TRANSFER' | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [auditingAccount, setAuditingAccount] = useState<Account | null>(null);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCreditCardReport, setShowCreditCardReport] = useState(false);
  const [showSystemLogs, setShowSystemLogs] = useState(false);
  const [showPlanning, setShowPlanning] = useState(false);
  const [showAccounting, setShowAccounting] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const [showCashCount, setShowCashCount] = useState(false);
  const [showSystemSettings, setShowSystemSettings] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showZeroBalances, setShowZeroBalances] = useState(false);
  
  const sidebarRef = useRef<HTMLDivElement>(null);
  const mainGridRef = useRef<HTMLDivElement>(null);
  const transactionsContainerRef = useRef<HTMLDivElement>(null);
  const [transactionLimit, setTransactionLimit] = useState(5);

  useEffect(() => {
    const updateLimit = () => {
      if (!sidebarRef.current || !transactionsContainerRef.current) return;
      
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const transactionsRect = transactionsContainerRef.current.getBoundingClientRect();
      
      // Detectar si están apilados (el sidebar de cuentas está debajo de las transacciones)
      // O si el ancho de pantalla es menor al breakpoint lg (1024px)
      // O si es un dispositivo táctil (iPads, tablets)
      const isStacked = sidebarRect.top > transactionsRect.top;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      if (isStacked || window.innerWidth < 1024 || isTouchDevice) {
        setTransactionLimit(15);
        return;
      }
      
      // La altura disponible real es la diferencia entre el final de la columna de cuentas
      // y el inicio de la lista de transacciones.
      // sidebarRect.bottom es la coordenada Y del final de las cuentas.
      // transactionsRect.top es la coordenada Y donde empiezan las transacciones.
      const availableHeightForRows = sidebarRect.bottom - transactionsRect.top - 85; 
      
      // 73 es la altura promedio de una fila de transacción en px.
      const estimatedLimit = Math.max(3, Math.floor(availableHeightForRows / 73));
      
      setTransactionLimit(estimatedLimit);
    };

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateLimit);
    });

    // Observamos el grid principal para detectar cambios en CUALQUIERA de las dos columnas
    if (mainGridRef.current) {
      observer.observe(mainGridRef.current);
    }

    updateLimit();
    return () => observer.disconnect();
  }, [showZeroBalances, accounts.length]);

  useEffect(() => {
    if (effectiveUserId) {
      const unsubAccounts = subscribeToAccounts(effectiveUserId, setAccounts);
      const unsubTransactions = subscribeToTransactions(effectiveUserId, setTransactions);
      return () => {
        unsubAccounts();
        unsubTransactions();
      };
    }
  }, [effectiveUserId]);

  const totalBalance = accounts
    .filter(a => a.isActive !== false)
    .reduce((acc, curr) => acc + curr.balance, 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const handleEditTransaction = (tx: Transaction) => {
    setEditingTransaction(tx);
    setShowForm(tx.type);
  };

  const handleCloseForm = () => {
    setShowForm(null);
    setEditingTransaction(null);
  };

  const handleResetPeriod = async () => {
    if (!effectiveUserId) return;
    setIsResetting(true);
    try {
      await resetPeriod(effectiveUserId);
      setShowResetConfirm(false);
    } catch (error) {
      console.error("Error resetting period:", error);
      alert("Hubo un error al depurar el periodo.");
    } finally {
      setIsResetting(false);
    }
  };

  if (showPlanning) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <PlanningDashboard 
            onBack={() => setShowPlanning(false)} 
            accounts={accounts} 
          />
        </div>
      </div>
    );
  }

  if (showAccounting) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <AccountingDashboard 
            onBack={() => setShowAccounting(false)}
            financeAccounts={accounts}
          />
        </div>
      </div>
    );
  }

  if (showManagement) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <ManagementDashboard 
            onBack={() => setShowManagement(false)}
            accounts={accounts}
          />
        </div>
      </div>
    );
  }

  if (showSystemSettings) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <header className="flex items-center gap-4 mb-12">
            <button 
              onClick={() => setShowSystemSettings(false)}
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
          </header>
          <SystemSettingsPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Hola, {profile?.email.split('@')[0]}</h1>
            <p className="text-zinc-500 mt-1">Aquí tienes el resumen de tus finanzas.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3 md:justify-end">
            {user?.email === 'emiliot11@gmail.com' && (
              <button 
                onClick={() => setShowSystemLogs(true)}
                title="Logs del Sistema"
                className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-red-400 hover:text-red-300"
              >
                <Terminal size={20} />
              </button>
            )}
            {profile?.role === 'admin' && (
              <button 
                onClick={() => setShowResetConfirm(true)}
                title="Depurar Periodo"
                className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
              >
                <Archive size={20} />
              </button>
            )}
            <button 
              onClick={() => window.location.reload()}
              title="Sincronizar Datos"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
            >
              <RefreshCw size={20} />
            </button>
            <button 
              onClick={() => setShowPlanning(true)}
              title="Planificación y Flujo de Caja"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-indigo-400 hover:text-indigo-300"
            >
              <CalendarDays size={20} />
            </button>
            <button 
              onClick={() => setShowAccounting(true)}
              title="Módulo Contable"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-amber-400 hover:text-amber-300"
            >
              <BookOpen size={20} />
            </button>
            <button 
              onClick={() => setShowManagement(true)}
              title="Operaciones de Gestión"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-emerald-400 hover:text-emerald-300"
            >
              <Briefcase size={20} />
            </button>
            <button 
              onClick={() => setShowCashCount(true)}
              title="Arqueo de Caja"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-blue-400 hover:text-blue-300"
            >
              <Calculator size={20} />
            </button>
            <button 
              onClick={() => setShowUserManagement(true)}
              title="Cuentas Vinculadas"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
            >
              <LinkIcon size={20} />
            </button>
            <button 
              onClick={() => setShowSystemSettings(true)}
              title="Configuración del Sistema"
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
            >
              <Settings size={20} />
            </button>
            <button 
              onClick={() => signOut()}
              className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all text-zinc-400 hover:text-white"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" ref={mainGridRef}>
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.div 
                whileHover={{ y: -4 }}
                className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center border border-emerald-500/20">
                    <TrendingUp className="text-emerald-500" size={20} />
                  </div>
                  <span className="text-sm font-medium text-zinc-400">Total Balance</span>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(totalBalance)}</div>
              </motion.div>

              <motion.button 
                whileHover={{ y: -4 }}
                onClick={() => setShowForm('INCOME')}
                className="bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/20 p-6 rounded-2xl text-left transition-all group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Plus className="text-black" size={20} />
                  </div>
                  <span className="text-sm font-medium text-emerald-500">Nuevo Ingreso</span>
                </div>
                <div className="text-zinc-500 text-xs">Registra entradas de dinero</div>
              </motion.button>

              <motion.button 
                whileHover={{ y: -4 }}
                onClick={() => setShowForm('EXPENSE')}
                className="bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 p-6 rounded-2xl text-left transition-all group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Plus className="text-black" size={20} />
                  </div>
                  <span className="text-sm font-medium text-red-500">Nuevo Gasto</span>
                </div>
                <div className="text-zinc-500 text-xs">Registra salidas de dinero</div>
              </motion.button>
            </div>

            {/* AI Input Section */}
            <AITransactionInput 
              accounts={accounts} 
              transactions={transactions}
              onSuccess={() => {
                // Transactions will update automatically via subscription
              }} 
            />

            {/* Recent Transactions */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" ref={transactionsContainerRef}>
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="font-semibold">Transacciones Recientes</h2>
                  <button 
                    onClick={() => setShowHistory(true)}
                    className="text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white px-2 py-1 rounded-md transition-all font-bold uppercase tracking-wider"
                  >
                    Ver Historial
                  </button>
                </div>
                <button 
                  onClick={() => setShowForm('TRANSFER')}
                  className="text-sm text-emerald-500 hover:text-emerald-400 flex items-center gap-1.5 font-medium"
                >
                  <ArrowRightLeft size={16} />
                  Transferir
                </button>
              </div>
              <div className="divide-y divide-zinc-800">
                {transactions.length === 0 ? (
                  <div className="p-12 text-center text-zinc-500">No hay transacciones registradas.</div>
                ) : (
                  transactions.slice(0, transactionLimit).map((tx) => (
                    <button 
                      key={tx.id} 
                      onClick={() => handleEditTransaction(tx)}
                      className="w-full p-4 hover:bg-zinc-800/50 transition-all flex items-center justify-between group text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                          tx.type === 'INCOME' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' :
                          tx.type === 'EXPENSE' ? 'bg-red-500/10 border-red-500/20 text-red-500' :
                          'bg-blue-500/10 border-blue-500/20 text-blue-500'
                        }`}>
                          {tx.type === 'INCOME' ? <TrendingUp size={18} /> :
                           tx.type === 'EXPENSE' ? <TrendingDown size={18} /> :
                           <ArrowRightLeft size={18} />}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{tx.description}</div>
                          <div className="text-xs text-zinc-500">
                            {safeFormat(tx.date, "d 'de' MMMM, yyyy", 'Invalid Date')}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className={`font-semibold ${
                            tx.type === 'INCOME' ? 'text-emerald-500' :
                            tx.type === 'EXPENSE' ? 'text-red-500' :
                            'text-zinc-100'
                          }`}>
                            {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}
                            {formatCurrency(tx.details.reduce((sum, d) => sum + d.amount, 0) / (tx.type === 'TRANSFER' ? 2 : 1))}
                          </div>
                          <div className="text-[10px] text-zinc-600 uppercase tracking-wider font-bold">
                            {tx.type}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Accounts */}
          <div className="space-y-6" ref={sidebarRef}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold flex items-center gap-2">
                  <Wallet size={18} className="text-emerald-500" />
                  Mis Cuentas
                </h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setShowCreditCardReport(true)}
                    className="p-1.5 bg-amber-500/10 text-amber-500 hover:bg-amber-500 hover:text-black rounded-lg transition-all"
                    title="Informe de Tarjetas de Crédito"
                  >
                    <CreditCard size={16} />
                  </button>
                  <button 
                    onClick={() => setShowZeroBalances(!showZeroBalances)}
                    className={`p-1.5 rounded-lg transition-all border ${
                      showZeroBalances 
                        ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' 
                        : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:text-zinc-300'
                    }`}
                    title={showZeroBalances ? "Ocultar saldos en cero" : "Mostrar todos los saldos"}
                  >
                    {showZeroBalances ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button 
                    onClick={() => setShowAddAccount(true)}
                    className="p-1.5 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-black rounded-lg transition-all"
                    title="Nuevo Medio de Pago"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <div className="space-y-6">
                {Array.from(new Set(accounts.map(a => a.type))).sort().map(type => {
                  const typeAccounts = accounts
                    .filter(a => a.type === type)
                    .filter(a => showZeroBalances ? true : (Math.abs(a.balance) > 0.01 && a.isActive !== false));
                  
                  if (typeAccounts.length === 0) return null;
                  const typeSubtotal = typeAccounts.reduce((sum, a) => sum + a.balance, 0);
                  
                  // Agrupar por institución
                  const instGroups = typeAccounts.reduce((acc, curr) => {
                    const instId = curr.institutionId || 'otros';
                    if (!acc[instId]) acc[instId] = [];
                    acc[instId].push(curr);
                    return acc;
                  }, {} as Record<string, Account[]>);
                  
                  return (
                    <div key={type} className="space-y-4">
                      <div className="flex items-center justify-between px-1 border-b border-zinc-800/50 pb-2">
                        <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">{type}</span>
                        <span className="text-[11px] font-bold text-zinc-300">{formatCurrency(typeSubtotal)}</span>
                      </div>
                      
                      <div className="space-y-4">
                        {Object.entries(instGroups).sort(([idA], [idB]) => {
                          const nameA = getInstitutionById(idA)?.name || 'Otra Entidad';
                          const nameB = getInstitutionById(idB)?.name || 'Otra Entidad';
                          return nameA.localeCompare(nameB);
                        }).map(([instId, instAccounts]: [string, Account[]]) => {
                          const instSubtotal = instAccounts.reduce((sum, a) => sum + a.balance, 0);
                          const instName = getInstitutionById(instId)?.name || 'Otra Entidad';

                          return (
                            <div key={instId} className="space-y-2">
                              <div className="flex items-center justify-between px-2">
                                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">{instName}</span>
                                <span className="text-[10px] font-bold text-zinc-500">{formatCurrency(instSubtotal)}</span>
                              </div>
                              
                              <div className="space-y-1">
                                {instAccounts.map((acc) => (
                                  <div key={acc.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-zinc-800/50 transition-all border border-transparent hover:border-zinc-700 group">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center text-zinc-400">
                                        <AccountIcon institutionId={acc.institutionId} type={acc.type} size={16} />
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium flex items-center gap-2">
                                          {acc.name}
                                          {acc.isActive === false && (
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded animate-pulse">
                                              Baja
                                            </span>
                                          )}
                                        </div>
                                        {(acc.type === 'TARJETA DE CREDITO' || acc.type === 'BANCOS') && (acc.creditLimit || acc.overdraftType !== 'NONE') && (
                                          <div className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
                                            {acc.creditLimit ? (
                                              <span title="Línea Principal">
                                                Línea: {new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(acc.creditLimit)}
                                              </span>
                                            ) : null}
                                            {acc.overdraftType !== 'NONE' && (
                                              <span title="Sobregiro / Excedente" className="text-amber-500/70 border-l border-zinc-700 pl-2">
                                                SG: {new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(
                                                  acc.overdraftType === 'FIXED' ? (acc.overdraftValue || 0) : 
                                                  acc.overdraftType === 'PERCENTAGE' ? ((acc.creditLimit || 0) * (acc.overdraftValue || 0) / 100) : 0
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-sm font-semibold text-right">
                                        <div>{formatCurrency(acc.balance)}</div>
                                      </div>
                                      <div className="flex items-center md:opacity-0 md:group-hover:opacity-100 transition-all">
                                        <button 
                                          onClick={() => setEditingAccount(acc)}
                                          className="p-1.5 text-zinc-600 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-all"
                                          title="Editar Cuenta"
                                        >
                                          <Edit2 size={14} />
                                        </button>
                                        <button 
                                          onClick={() => setAuditingAccount(acc)}
                                          className="p-1.5 text-zinc-600 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-all"
                                          title="Auditar Cuenta"
                                        >
                                          <History size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
 
      {/* Transaction Form Modal */}
      <AnimatePresence>
        {showForm && (
          <TransactionForm 
            type={showForm} 
            accounts={accounts} 
            onClose={handleCloseForm}
            editTransaction={editingTransaction || undefined}
          />
        )}
        {showUserManagement && (
          <UserManagement onClose={() => setShowUserManagement(false)} />
        )}
        {auditingAccount && (
          <AccountAudit 
            account={accounts.find(a => a.id === auditingAccount.id) || auditingAccount} 
            onClose={() => setAuditingAccount(null)} 
          />
        )}
        {editingAccount && (
          <EditAccountModal
            account={editingAccount}
            onClose={() => setEditingAccount(null)}
          />
        )}
        {showAddAccount && (
          <AddAccountModal
            onClose={() => setShowAddAccount(false)}
          />
        )}

        {showHistory && (
          <TransactionHistory 
            transactions={transactions} 
            accounts={accounts} 
            onClose={() => setShowHistory(false)} 
            onEdit={(tx) => {
              setShowHistory(false);
              handleEditTransaction(tx);
            }}
          />
        )}
        {showCreditCardReport && (
          <CreditCardReport
            accounts={accounts}
            onClose={() => setShowCreditCardReport(false)}
          />
        )}
        {showResetConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center gap-4 mb-6 text-red-500">
                <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Depurar Periodo</h2>
                  <p className="text-sm text-red-400">Acción destructiva</p>
                </div>
              </div>
              
              <div className="space-y-4 text-zinc-300 text-sm mb-8">
                <p>
                  Esta acción realizará lo siguiente:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-zinc-400">
                  <li>Recalculará el <strong>saldo inicial</strong> de todas las cuentas basándose en el saldo actual.</li>
                  <li><strong>Eliminará permanentemente</strong> todas las transacciones registradas hasta el momento.</li>
                  <li>Iniciará un nuevo periodo en limpio.</li>
                </ul>
                <p className="text-amber-400 font-medium mt-4">
                  Asegúrate de haber exportado tus datos antes de continuar. Esta acción no se puede deshacer.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(false)}
                  disabled={isResetting}
                  className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleResetPeriod}
                  disabled={isResetting}
                  className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      Depurando...
                    </>
                  ) : (
                    'Sí, depurar ahora'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showSystemLogs && (
          <SystemLogsViewer onClose={() => setShowSystemLogs(false)} />
        )}
        {showCashCount && (
          <CashCountTool onClose={() => setShowCashCount(false)} />
        )}
      </AnimatePresence>

      {/* Footer / Version Indicator */}
      <footer className="max-w-7xl mx-auto mt-12 pt-6 border-t border-zinc-800/50 flex flex-col items-center justify-center gap-2">
        <a
          href="https://www.hinapsys.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 opacity-30 hover:opacity-60 transition-opacity"
        >
          <span className="text-[10px] text-zinc-500 font-medium font-sans">Powered by</span>
          <img
            src="https://hinapsys-storage.ewr1.vultrobjects.com/public/shared/logos/wordmark_white.svg"
            alt="HINAPSYS"
            className="h-3"
          />
        </a>
        <div className="text-xs text-zinc-600 font-mono flex items-center gap-2">
          <span>Finanzas PRO</span>
          <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
          <span>v{__APP_VERSION__}</span>
        </div>
      </footer>
    </div>
  );
};
